import {Object3D} from 'three'
import {IThreeJsLayer} from './interfaces/IThreeJsLayer'
import {IObjectLoader} from './interfaces/IObjectLoader'

/**
 * Абстрактный базовый класс для всех загрузчиков объектов
 * Реализует общую функциональность и использует Template Method паттерн
 */
export abstract class BaseLoader implements IObjectLoader {
  protected threeLayer!: IThreeJsLayer
  protected readonly objectsMap: Map<string, Object3D> = new Map()

  /**
   * Инициализация Three.js слоя
   */
  initThreeLayer(threeLayer: IThreeJsLayer): void {
    this.threeLayer = threeLayer
  }

  /**
   * Ожидание загрузки всех ресурсов
   * Переопределяется в подклассах для конкретной логики загрузки
   */
  abstract waitForLoad(): Promise<void>

  /**
   * Очистка всех объектов
   * Template Method - общая логика, может быть переопределена
   * Подклассы могут переопределить onBeforeClear() для дополнительной логики
   */
  clearAll(): void {
    this.onBeforeClear()
    this.objectsMap.forEach((_, id) => {
      this.threeLayer?.removeObject(id)
    })
    this.objectsMap.clear()
    this.onAfterClear()
  }

  /**
   * Хук вызывается перед очисткой объектов
   * Переопределяется в подклассах для дополнительной логики (например, остановка анимации)
   */
  protected onBeforeClear(): void {
    // Переопределяется в подклассах при необходимости
  }

  /**
   * Хук вызывается после очистки объектов
   * Переопределяется в подклассах для дополнительной логики
   */
  protected onAfterClear(): void {
    // Переопределяется в подклассах при необходимости
  }

  /**
   * Получение объекта по ID
   */
  protected getObject(id: string): Object3D | undefined {
    return this.objectsMap.get(id)
  }

  /**
   * Добавление объекта в коллекцию
   */
  protected addObjectToCollection(id: string, object: Object3D): void {
    this.objectsMap.set(id, object)
  }

  /**
   * Удаление объекта из коллекции
   */
  protected removeObjectFromCollection(id: string): void {
    this.objectsMap.delete(id)
  }

  /**
   * Получение всех объектов
   */
  protected getAllObjects(): ReadonlyMap<string, Object3D> {
    return this.objectsMap
  }

  /**
   * Проверка наличия Three.js слоя
   */
  protected hasThreeLayer(): boolean {
    return !!this.threeLayer
  }

  /**
   * Универсальный метод для получения объектов в виде Record (для обратной совместимости)
   * Защищает от изменения коллекции извне через Object.freeze
   * @template T Тип объекта (по умолчанию Object3D)
   * @returns Readonly Record с объектами
   */
  protected getObjectsAsRecord<T extends Object3D = Object3D>(): Readonly<
    Record<string, T>
  > {
    const result: Record<string, T> = {}
    this.objectsMap.forEach((obj, id) => {
      result[id] = obj as T
    })
    return Object.freeze(result) as Readonly<Record<string, T>>
  }
}
